import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Modal from '@/components/Modal'
import NameCombobox from '@/components/NameCombobox'
import { api } from '@/lib/api'
import {
  APPOINTMENT_STATUS_LABELS,
  listUsers,
  type Appointment,
  type AppointmentCreatePayload,
  type AppointmentUpdatePayload,
} from '@/lib/appointments'

export type AppointmentModalState =
  | { mode: 'create'; start: Date; end: Date }
  | { mode: 'edit'; appointment: Appointment }
  | null

interface Props {
  state: AppointmentModalState
  onClose: () => void
  onCreate: (payload: AppointmentCreatePayload) => void
  onUpdate: (id: number, patch: AppointmentUpdatePayload) => void
  saving: boolean
}

type PatientsResponse = {
  patients: { id: number; hn: string; nameTh: string }[]
}

// "HH:mm" options 08:00..20:00 every 30 min. 20:00 is a valid *end* time;
// the start select skips it.
const TIMES: string[] = []
for (let h = 8; h <= 20; h++) {
  TIMES.push(`${String(h).padStart(2, '0')}:00`)
  if (h < 20) TIMES.push(`${String(h).padStart(2, '0')}:30`)
}

// Tapping a status button saves immediately — the fastest path for the
// front-desk "patient just walked in" flow.
const STATUS_ACTIVE_CLASSES: Record<'booked' | 'arrived' | 'no_show', string> = {
  booked: 'border-[#204687] bg-[#204687] text-white',
  arrived: 'border-green-600 bg-green-600 text-white',
  no_show: 'border-red-600 bg-red-600 text-white',
}

const toDateInput = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const toTimeInput = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

export default function AppointmentModal({ state, onClose, onCreate, onUpdate, saving }: Props) {
  const [patientName, setPatientName] = useState('')
  const [patientId, setPatientId] = useState<number | null>(null)
  const [therapistName, setTherapistName] = useState('')
  const [therapistId, setTherapistId] = useState<number | null>(null)
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('09:30')
  const [note, setNote] = useState('')
  const [status, setStatus] = useState<Appointment['status']>('booked')
  const [error, setError] = useState('')

  // Re-seed the form whenever the modal (re)opens.
  useEffect(() => {
    if (!state) return
    setError('')
    if (state.mode === 'create') {
      setPatientName('')
      setPatientId(null)
      setTherapistName('')
      setTherapistId(null)
      setDate(toDateInput(state.start))
      setStartTime(toTimeInput(state.start))
      setEndTime(toTimeInput(state.end))
      setNote('')
      setStatus('booked')
    } else {
      const a = state.appointment
      const s = new Date(a.startAt)
      const e = new Date(a.endAt)
      setPatientName(a.patientName)
      setPatientId(a.patientId)
      setTherapistName(a.therapistName)
      setTherapistId(a.therapistId)
      setDate(toDateInput(s))
      setStartTime(toTimeInput(s))
      setEndTime(toTimeInput(e))
      setNote(a.note ?? '')
      setStatus(a.status)
    }
  }, [state])

  const patientQuery = patientName.trim()
  const { data: patientData } = useQuery<PatientsResponse>({
    queryKey: ['patients-suggest', patientQuery],
    queryFn: () => api<PatientsResponse>(`/patients?q=${encodeURIComponent(patientQuery)}&limit=8`),
    enabled: !!state && patientQuery.length >= 2,
    staleTime: 30_000,
  })
  const { data: usersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: listUsers,
    enabled: !!state,
    staleTime: 5 * 60_000,
  })

  const patientOptions = useMemo(
    () =>
      (patientData?.patients ?? []).map((p) => ({ id: p.id, name: p.nameTh, hint: `HN ${p.hn}` })),
    [patientData],
  )
  const therapistOptions = useMemo(
    () => (usersData?.users ?? []).map((u) => ({ id: u.id, name: u.username })),
    [usersData],
  )

  if (!state) return null

  function buildDates(): { startAt: string; endAt: string } | null {
    const start = new Date(`${date}T${startTime}:00`)
    const end = new Date(`${date}T${endTime}:00`)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
    if (end.getTime() <= start.getTime()) return null
    return { startAt: start.toISOString(), endAt: end.toISOString() }
  }

  function submit() {
    if (!patientName.trim()) return setError('กรุณาใส่ชื่อคนไข้')
    if (!therapistName.trim()) return setError('กรุณาใส่ชื่อนักกายภาพ')
    const dates = buildDates()
    if (!dates) return setError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม')
    const common = {
      patientId,
      patientName: patientName.trim(),
      therapistId,
      therapistName: therapistName.trim(),
      ...dates,
      note: note.trim() || null,
    }
    // Status changes go through the quick buttons only — the save button
    // must not accidentally revert a status set after the form was opened.
    if (state!.mode === 'create') onCreate(common)
    else onUpdate(state!.appointment.id, common)
  }

  const isEdit = state.mode === 'edit'

  return (
    <Modal open onClose={onClose} title={isEdit ? 'แก้ไขนัดหมาย' : 'สร้างนัดหมาย'}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ชื่อคนไข้</label>
          <NameCombobox
            value={patientName}
            onChange={(name, id) => {
              setPatientName(name)
              setPatientId(id)
            }}
            options={patientOptions}
            placeholder="พิมพ์ชื่อคนไข้…"
            autoFocus={!isEdit}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">นักกายภาพผู้ทำ</label>
          <NameCombobox
            value={therapistName}
            onChange={(name, id) => {
              setTherapistName(name)
              setTherapistId(id)
            }}
            options={therapistOptions}
            placeholder="พิมพ์ชื่อนักกายภาพ…"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">วันที่</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">เริ่ม</label>
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-2"
            >
              {TIMES.filter((t) => t !== '20:00').map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">สิ้นสุด</label>
            <select
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-2"
            >
              {TIMES.filter((t) => t !== '08:00').map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">สถานะ</label>
            <div className="flex gap-2">
              {(['booked', 'arrived', 'no_show'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={saving}
                  onClick={() => onUpdate(state.appointment.id, { status: s })}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${
                    status === s
                      ? STATUS_ACTIVE_CLASSES[s]
                      : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {APPOINTMENT_STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-400">กดสถานะแล้วบันทึกทันที</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">โน้ต</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-between pt-2">
          {isEdit ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                if (confirm('ลบนัดนี้ออกจากปฏิทินใช่ไหม?')) {
                  onUpdate(state.appointment.id, { status: 'cancelled' })
                }
              }}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              🗑 ลบนัด
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
            >
              ปิด
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={submit}
              className="rounded-lg bg-[#204687] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
